import React from "react";
import { Link } from "react-router-dom";

// Real repair stories — problem, fix, result. Builds trust and demonstrates expertise.
const caseStudies = [
  {
    customer: "Sarah M.",
    location: "Horsham, PA",
    device: "iPhone 14 Pro",
    icon: "fab fa-apple",
    problem:
      "Dropped my iPhone at a concert. Screen completely shattered — I couldn't see anything. Apple Store quoted $349 with a 3-week wait.",
    fix: "Diagnosed on the spot. Full OLED screen replacement using premium OEM-compatible parts. Face ID preserved. Completed in 45 minutes while customer waited.",
    result: "$219 total — $130 cheaper than Apple. Same-day. 30-day warranty. Sarah was back at the concert the same evening.",
    timeFrame: "45 minutes",
    savings: "Saved $130",
    serviceLink: "/services/iphone-repair",
  },
  {
    customer: "David & Kim R.",
    location: "Abington, PA",
    device: "Samsung Galaxy S23 Ultra",
    icon: "fab fa-android",
    problem:
      "Phone fell in the pool at a family pool party. Turned off for an hour. Wife was devastated — 3 years of family photos at risk.",
    fix: "Immediate ultrasonic cleaning to remove chlorinated water. 24-hour drying cycle. Replaced 2 damaged flex cables. Verified display, cameras, charging, Wi-Fi all working.",
    result: "All 4,200 photos intact. Phone working like new. David called it 'nothing short of a miracle.' Total: $179 + a very happy anniversary present.",
    timeFrame: "48 hours",
    savings: "Saved all data",
    serviceLink: "/services/water-damage-repair",
  },
  {
    customer: "Marcus T.",
    location: "Philadelphia, PA",
    device: "MacBook Pro 13\" (2021)",
    icon: "fas fa-laptop",
    problem:
      "Coffee spill on keyboard. MacBook wouldn't turn on. Important thesis due in 48 hours. Apple Store said 'unrepairable, replace for $1,499.'",
    fix: "Free diagnostic revealed only 2 damaged logic board components (not the SSD). Microsoldered the repair. Recovered all files. Cleaned keyboard assembly.",
    result: "Fully working MacBook + thesis submitted on time. Repair cost: $389 — 74% less than replacement. Marcus passed his defense.",
    timeFrame: "36 hours",
    savings: "Saved $1,110",
    serviceLink: "/services/data-recovery",
  },
];

const RepairCaseStudies = () => {
  return (
    <section
      style={{
        padding: "60px 0",
        background: "#f8f9fa",
      }}
      aria-label="Real customer repair stories"
    >
      <div className="container">
        {/* Header */}
        <div className="text-center mb-40">
          <div
            style={{
              display: "inline-block",
              background: "rgba(24, 48, 93, 0.08)",
              color: "#18305d",
              padding: "5px 16px",
              borderRadius: "20px",
              fontSize: "0.78rem",
              fontWeight: 700,
              marginBottom: "15px",
              letterSpacing: "0.5px",
            }}
          >
            <i className="fas fa-file-alt mr-2" />
            REPAIR STORIES · REAL RESULTS
          </div>
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: 800,
              color: "#18305d",
              marginBottom: "12px",
            }}
          >
            Problem → Fix → Result
          </h2>
          <p
            style={{
              fontSize: "1.05rem",
              color: "#666",
              maxWidth: "680px",
              margin: "0 auto",
              lineHeight: 1.6,
            }}
          >
            Three real stories from customers who thought their device was beyond saving.
            Here's what happened when they walked into Cell Station.
          </p>
        </div>

        {/* Case studies */}
        <div className="row">
          {caseStudies.map((cs, idx) => (
            <div key={idx} className="col-lg-4 col-md-6 mb-30">
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: "16px",
                  padding: "28px",
                  height: "100%",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                  border: "1px solid #e9ecef",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: "16px", gap: "12px" }}>
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #F50963 0%, #c40851 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <i className={cs.icon} style={{ color: "#fff", fontSize: "1.3rem" }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: "#18305d", fontSize: "1rem" }}>
                      {cs.customer}
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "#888" }}>
                      <i className="fas fa-map-marker-alt" style={{ color: "#F50963", marginRight: "4px" }} />
                      {cs.location} · {cs.device}
                    </div>
                  </div>
                </div>

                {/* Problem */}
                <div style={{ marginBottom: "14px" }}>
                  <div
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 800,
                      color: "#dc3545",
                      letterSpacing: "0.5px",
                      marginBottom: "6px",
                    }}
                  >
                    <i className="fas fa-exclamation-triangle mr-1" />
                    THE PROBLEM
                  </div>
                  <p style={{ fontSize: "0.9rem", color: "#555", lineHeight: 1.55, margin: 0 }}>
                    {cs.problem}
                  </p>
                </div>

                {/* Fix */}
                <div style={{ marginBottom: "14px" }}>
                  <div
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 800,
                      color: "#007bff",
                      letterSpacing: "0.5px",
                      marginBottom: "6px",
                    }}
                  >
                    <i className="fas fa-tools mr-1" />
                    THE FIX
                  </div>
                  <p style={{ fontSize: "0.9rem", color: "#555", lineHeight: 1.55, margin: 0 }}>
                    {cs.fix}
                  </p>
                </div>

                {/* Result */}
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 800,
                      color: "#28a745",
                      letterSpacing: "0.5px",
                      marginBottom: "6px",
                    }}
                  >
                    <i className="fas fa-check-circle mr-1" />
                    THE RESULT
                  </div>
                  <p style={{ fontSize: "0.9rem", color: "#333", lineHeight: 1.55, margin: 0, fontWeight: 500 }}>
                    {cs.result}
                  </p>
                </div>

                {/* Stats */}
                <div
                  style={{
                    marginTop: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "12px 14px",
                    background: "#fff9e6",
                    borderRadius: "10px",
                    border: "1px solid #ffe5a8",
                    marginBottom: "14px",
                  }}
                >
                  <div>
                    <div style={{ fontSize: "0.65rem", color: "#8b6914", fontWeight: 700, letterSpacing: "0.4px" }}>
                      TURNAROUND
                    </div>
                    <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "#18305d" }}>
                      {cs.timeFrame}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "0.65rem", color: "#8b6914", fontWeight: 700, letterSpacing: "0.4px" }}>
                      OUTCOME
                    </div>
                    <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "#28a745" }}>
                      {cs.savings}
                    </div>
                  </div>
                </div>

                <Link
                  to={cs.serviceLink}
                  style={{
                    display: "block",
                    textAlign: "center",
                    padding: "10px",
                    background: "#18305d",
                    color: "#fff",
                    borderRadius: "8px",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  See similar repair →
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-20">
          <Link
            to="/contact-us"
            className="btn btn-primary"
            style={{ padding: "14px 36px", fontSize: "1rem", fontWeight: 700 }}
          >
            <i className="fas fa-bolt mr-2" />
            Get Your Device Fixed
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RepairCaseStudies;
